import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from "react-helmet";
import moment from 'moment-timezone';

// Context
import { Consumer } from '../../context';

// Components
import InformesControls from './InformesControls';
import Header from '../../components/header/header';
import SidebarAdministracion from '../../components/header/sidebarAdministracion';
import PaginadorParent, { paginadorFilter } from '../../components/paginador/PaginadorParent';
import PagosYSafModal from './components/PagosYSafModal';
import { MoneyFormat, TableMoneyFormat, TablePercentageFormat } from './components/TableFormats';

// Helpers
import { applyDetalladoFilters, applyConcentradoFilters,
    getDetalladoTotalesYPagosActual, getConcentradoTotalesYPagosActual, 
    isPagoInDateRange 
} from './helpers/consultasHelper';

// Requests
import { desgloseTipos } from './hooks/useInformesParameters';

const informesInfoInit = () => {
    const todayDate = new Date();

    const pagosFechasDesdeInit = new Date(todayDate.getFullYear(), todayDate.getMonth(), 1);
    const pagosFechasHastaInit = new Date(todayDate.getFullYear(), todayDate.getMonth() + 1, 0);

    return {
        informes: [],
        headersInfo: [],
        filters: {
            desglose: 0,
            vivsIds: undefined,
            prodsIds: undefined,
            foliosIds: undefined,
            pagoEstado: 0,
            vacios: 0,
            pagosFechas: { desde: pagosFechasDesdeInit, hasta: pagosFechasHastaInit },
        },
        tableSettings: {
            anclarColumnas: false,
            anclarFilas: true,
            desglosarTotales: true
        }
    }
};

const initWidthPercentage = 0.8;
const containerWidthPercentage = 0.9;
const initWidthContent = initWidthPercentage * 100;

const getTableMaxWidth = (otherPercentage = 1) => 
    document.body.clientWidth * containerWidthPercentage * otherPercentage

function Informes(props) {

    const { context, location } = props;
    const { getSubmenuAdminName, submenuAdminName } = context;

    const [informesInfo, setInformesInfo] = useState(informesInfoInit);
    const [tablesMaxHeight, setTablesMaxHeight] = useState(0);
    const [contentWidth, setContentWidth] = useState(initWidthContent + "%");

    const contentRef = useRef();
    const initContentHeight = useRef(null);

    const { informes, filters, headersInfo } = informesInfo;
    const { desglose, pagosFechas } = filters;

    if(initContentHeight.current === null) {
        const contentElement = contentRef.current?.children[0];
        if(contentElement) initContentHeight.current = contentElement.clientHeight;
    };

    useEffect(() => {

        const sidebar = document.getElementById('sidebar');
        const informesTable = contentRef.current.querySelector('#informes-tables');

        const config = { attributes: true };

        const onSidebarMutation = (mutationList, observer) => {

            const isSideBarActive =  mutationList.some(({ attributeName, target }) => 
                attributeName !== 'class' && target.className.includes('active')
            );

            const { maxWidth, contentWidth } = isSideBarActive ? 
                { maxWidth: getTableMaxWidth(initWidthPercentage), contentWidth: initWidthContent + '%' }
                : { maxWidth: getTableMaxWidth(), contentWidth: '100%' }

            informesTable.style.setProperty("--table-max-width", maxWidth + "px");
            setContentWidth(contentWidth);

        };

        const maxWidth = getTableMaxWidth(initWidthPercentage);
        informesTable.style.setProperty("--table-max-width", maxWidth + "px");

        const observer = new MutationObserver(onSidebarMutation);

        observer.observe(sidebar, config);

        return () => observer.disconnect();

    }, []);

    useEffect(() => {

        getSubmenuAdminName(location.pathname);

    }, [getSubmenuAdminName, location]);

    const onControlsSizeChange = useCallback(({ height }) => {

        const newMaxHeight = initContentHeight.current - height;
        setTablesMaxHeight(newMaxHeight);
    }, []);

    const onFiltersChanged = useCallback((filters) => setInformesInfo(prevInfo => {

        const { headersInfo, ...otherFilters } = filters;

        const newInfo = { ...prevInfo };

        const newFilters = { ...newInfo.filters, ...otherFilters };

        Object.keys(newFilters).forEach((key) => newFilters[key] === undefined && delete newFilters[key]);

        newInfo.filters = newFilters;
        if (headersInfo) newInfo.headersInfo = headersInfo;

        return newInfo;
    }), []);

    const onTableSettingsChanged = (settings) => setInformesInfo(prevInfo => {

        const newInfo = { ...prevInfo };
        const newSettings = { ...newInfo.tableSettings, ...settings };

        newInfo.tableSettings = newSettings;

        return newInfo;
    });

    const onInformesChanged = useCallback((informes, filters) => setInformesInfo(prevInfo => {

        const { headersInfo, ...otherFilters } = filters;

        const newInfo = { ...prevInfo };

        const newFilters = { ...newInfo.filters, ...otherFilters };

        Object.keys(newFilters).forEach((key) => newFilters[key] === undefined && delete newFilters[key]);

        newInfo.informes = informes;
        newInfo.filters = newFilters;
        newInfo.headersInfo = headersInfo;

        return newInfo;
    }), []);

    const { filteredInformes, extraInfo } = useMemo(() => {

        const enabledHeaders = headersInfo ? headersInfo.filter((({ disabled }) => !disabled)) : [];

        const filteredInformes = informes.length === 0 ? null
            : filters.desglose === 0 ?
                applyDetalladoFilters({ informes, filters })
                : applyConcentradoFilters({ informes, filters });

        const extraInfo = filters.desglose === desgloseTipos.Detallado ?
            getDetalladoTotalesYPagosActual(filteredInformes || [], filters.pagosFechas) 
            : getConcentradoTotalesYPagosActual(filteredInformes || [], enabledHeaders, filters.pagosFechas);

        return { enabledHeaders, filteredInformes, extraInfo };

    }, [informes, filters, headersInfo]);
    
    return (
        <div className="column" /*admin*/>
            <Helmet>
                <title>{process.env.REACT_APP_NAME} - {submenuAdminName}</title>
            </Helmet>
            <Header
                id='header'
                reload={() => console.log("Add reload")}
                change={() => console.log("Add change")}
                sidebar={true}
                nav={"administracion"}
            />
            <div className="row full">
                <div className="column" id="sidebar">
                    <SidebarAdministracion />
                </div>
                <div id="content"
                    ref={contentRef}
                    className="column align-center" 
                    style={{ width: contentWidth }}
                >
                    <div
                        className="container column"
                        style={{ flex: 1 }}
                    >
                        <InformesControls
                            title={submenuAdminName}
                            excelData={filteredInformes}
                            onSizeChange={onControlsSizeChange}
                            onInformes={onInformesChanged}
                            onFiltersChanged={onFiltersChanged}
                            onTableSettingsChanged={onTableSettingsChanged}
                        />
                        <InformesTableSelector
                            informes={filteredInformes}
                            desglose={desglose}
                            pagosFechas={pagosFechas}
                            headersInfo={headersInfo}
                            extraInfo={extraInfo}
                            tableSettings={informesInfo.tableSettings}
                            tablesMaxHeight={tablesMaxHeight}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Consumer(Informes);

const InformesTableSelector = (props) => {

    const { informes, desglose, pagosFechas, headersInfo, extraInfo, tableSettings, tablesMaxHeight } = props;

    const [{ page, elementsPerPage }, setPaginadorInfo] = useState({ page: 0, elementsPerPage: 50 });
    const [enableShadow, setEnableShadow] = useState(false);

    const onPagedDataChanged = ({ page, elementsPerPage }) => setPaginadorInfo(prevInfo => {

        const { page: prevPage, elementsPerPage: prevElements } = prevInfo;

        if(page === prevPage && elementsPerPage === prevElements) return prevInfo;

        return { ...prevInfo, page, elementsPerPage };
    });

    const prePagedInformes = informes || [];
    const pagedInformes = paginadorFilter(prePagedInformes, page, elementsPerPage);
    const pagedPagosActual = paginadorFilter(extraInfo.pagosActual || [], page, elementsPerPage);

    const haveInformes = informes !== null;

    const setTableResizeObserver = useCallback(() => {

        const tableContainer = document.getElementById('informes-tables')
            .getElementsByClassName('informes-selector')[0];

        const table = tableContainer.querySelector('table');

        const observer = new ResizeObserver((entries) => {

            if(entries[0]) {
                const { clientWidth, scrollWidth } = tableContainer;
                setEnableShadow(clientWidth < scrollWidth)
            }
        })

        observer.observe(table, { box: "border-box" });

        return observer;
    }, []);

    useEffect(() => {

        if(!haveInformes) return;

        const observer = setTableResizeObserver();

        return () => observer.disconnect();

    }, [haveInformes, desglose, setTableResizeObserver]);
    
    return (
        <div
            id="informes-tables"
            style={{
                display: "flex", flexDirection: "column", flex: 1, backgroundColor: "#FFF", zIndex: 0,
                maxHeight: tablesMaxHeight || "auto",
                transition: "all 300ms ease-in-out",
            }}
        >
            {informes !== null ?
                <>
                    <div className="column full informes-selector">
                        {desglose === desgloseTipos.Detallado ?
                            <DetalladoTable
                                informes={pagedInformes}
                                pagosFechas={pagosFechas}
                                tableSettings={tableSettings}
                                enableShadow={enableShadow}
                                extraInfo={extraInfo}
                                pagosActual={pagedPagosActual}
                            />
                            : <ConcentradoTable
                                informes={pagedInformes}
                                pagosFechas={pagosFechas}
                                tableSettings={tableSettings}
                                enableShadow={enableShadow}
                                headersInfo={headersInfo}
                                extraInfo={extraInfo}
                                pagosActual={pagedPagosActual}
                            />
                        }
                    </div>
                    <PaginadorParent
                        data={prePagedInformes}
                        initalElementsPerPage={50}
                        onPagedDataChange={onPagedDataChanged}
                    />
                </>
                :
                <div className='row full justify-center align-center' style={{ flex: 1 }}>
                    <h3>Sin documentos en busqueda</h3>
                </div>
            }
        </div>
    )
};

const idModalContainer = 'pagos-modal-open';

const PagoCell = (props) => {
    
    const { id, pago, onPointerEnter } = props;

    return (
        <td
            id={id}
            className="pagos-td text-right font-mini"
        >
            <div className='row' style={{ justifyContent: "flex-end", gap: 'calc(0.6rem + 6px)' }} >
                <MoneyFormat value={pago} />
                <button
                    className="btn-info"
                    type="button"
                    onPointerEnter={onPointerEnter}
                >
                    <i className="font-small fas fa-info-circle pagos-i" style={{ display: "flex", alignSelf: "center" }} />
                </button>
            </div>
        </td>
    );
}

const DocumentoRow = (props) => {

    const { documento, pagoActual, modalOpen, onPagoInfoEnter } = props;
    const { id_docCobro, vivienda, num_folio, producto, mes_anyo,
        cargo, descuento, condonacion, recargo, saldo
    } = documento;

    const textAlignCenter = { textAlign: "center" };
    const textAlignLeft = { textAlign: "left" };
    
    return (
        <tr>
            <td style={textAlignLeft}>{vivienda}</td>
            <td style={textAlignLeft}>{num_folio}</td>
            <td style={textAlignLeft}>{producto}</td>
            <td style={{ whiteSpace: "nowrap", ...textAlignCenter }} >
                {mes_anyo}
            </td>
            <TableMoneyFormat value={cargo} />
            <TableMoneyFormat value={descuento} />
            <TableMoneyFormat value={condonacion} />
            <TableMoneyFormat value={recargo} />
            <PagoCell 
                id={modalOpen ? idModalContainer : undefined}
                pago={pagoActual}
                onPointerEnter={onPagoInfoEnter}
            />
            <TableMoneyFormat value={saldo} />
        </tr>
    );
};

const SaldoAFavorRow = (props) => {

    const { documento, pagoActual, modalOpen, onPagoInfoEnter } = props;

    const { id_vivienda, vivienda, producto, saf } = documento;

    const textAlignLeft = { textAlign: "left" };
    const borderRightNone = { borderRight: 'none' };

    return (
        <tr>
            <td style={textAlignLeft}>{vivienda}</td>
            <td style={borderRightNone}></td>
            <td colSpan={6} style={textAlignLeft}>{producto}</td>
            <PagoCell
                id={modalOpen ? idModalContainer : undefined}
                pago={pagoActual}
                onPointerEnter={onPagoInfoEnter}
            />
            <TableMoneyFormat value={-pagoActual} />            
        </tr>
    );
};

const getPagosMapped = (pagos, pagosFechas) => pagosFechas ? pagos.map((pago) => ({
    ...pago, disabled: !isPagoInDateRange(pago.fecha, pagosFechas)
})) : pagos

const getSafsMapped = (pagos, pagosFechas) => pagosFechas ? pagos.map((pago) => ({
        ...pago, disabled: moment(pago.fecha).isAfter(pagosFechas.hasta, 'days')
})) : pagos;

const DetalladoTable = (props) => {

    const { informes, pagosFechas, tableSettings, enableShadow, extraInfo, pagosActual } = props;
    const { colTotales } = extraInfo;
    const { anclarFilas } = tableSettings;

    const [pagosModalInfo, setPagosModalInfo] = useState({
        rowIndex: -1, open: false, 
        rect: { top: 0, left: 0, width: 0 }    
    });

    const { rowIndex, open, rect } = pagosModalInfo;

    const pagosMapped = informes[rowIndex] ? informes[rowIndex].pagos.length > 0 ? 
        getPagosMapped(informes[rowIndex].pagos, pagosFechas)
        : getSafsMapped(informes[rowIndex].saf, pagosFechas)
        : [];

    const enableAnclarFilas = anclarFilas ? "anclar-filas-totales" : "";
    const enabledShadow = enableShadow ? 'enable-shadow' : '';

    const tableClassName = ['detallados-table', enableAnclarFilas, enabledShadow].join(' ');

    const textAlignCenter = { textAlign: "center" };
    const textAlignLeft = { textAlign: "left" };

    const onPagoInfoEnter = (e, index) => {

        const bounds = e.target.getBoundingClientRect();

        setPagosModalInfo(({ rowIndex, rect }) => ({
            rowIndex: index, 
            open: true, 
            rect: bounds
        }));
    };

    const onCloseModal = () => setPagosModalInfo(({ rowIndex, open, rect }) => ({
        rowIndex, rect, open: false
    }));

    return (
        <>
            <table className={tableClassName}>
                <thead>
                    <tr>
                        <th>Vivienda</th>
                        <th>N° Folio</th>
                        <th>Producto</th>
                        <th style={textAlignCenter}>Mes</th>
                        <th style={textAlignCenter}>Importe</th>
                        <th style={textAlignCenter}>Descuento</th>
                        <th style={textAlignCenter}>Condonado</th>
                        <th style={textAlignCenter}>Recargos</th>
                        <th style={textAlignCenter}>Pago</th>
                        <th style={textAlignCenter}>Saldo</th>
                    </tr>
                </thead>
                <tbody>
                    {informes.length > 0 ?
                        <>
                            {informes.map((documento, index) => documento.num_folio === '' ?
                                <SaldoAFavorRow
                                    key={'saf-' + documento.id_vivienda}
                                    documento={documento}
                                    pagoActual={pagosActual[index]}
                                    modalOpen={rowIndex === index}
                                    onPagoInfoEnter={(e) => onPagoInfoEnter(e, index)}
                                />
                                :
                                <DocumentoRow
                                    key={documento.id_docCobro}
                                    documento={documento} 
                                    pagoActual={pagosActual[index]}
                                    modalOpen={rowIndex === index}
                                    onPagoInfoEnter={(e) => onPagoInfoEnter(e, index)}
                                />
                            )}
                            <tr>
                                <td style={textAlignLeft}>Totales</td>
                                <td colSpan={3} style={textAlignLeft}></td>
                                <TableMoneyFormat value={colTotales.Importes} />
                                <TableMoneyFormat value={colTotales.Descuentos} />
                                <TableMoneyFormat value={colTotales.Condonados} />
                                <TableMoneyFormat value={colTotales.Recargos} />
                                <TableMoneyFormat value={colTotales.Pagados} />
                                <TableMoneyFormat value={colTotales.Saldos} />
                            </tr>
                        </>
                        :
                        <tr className='empty-data-row'>
                            <td colSpan={10}
                                style={{ border: "unset", padding: 0 }}
                            >
                                <div >
                                    <h3>Sin documentos en filtrado</h3>
                                </div>
                            </td>
                        </tr>
                    }
                </tbody>
            </table>
            <PagosYSafModal
                isPagos={informes[rowIndex]?.pagos.length > 0}
                pagos={pagosMapped}
                open={open}
                parentInfo={{ id: idModalContainer, rect }}
                onCloseModal={onCloseModal}
            />
        </>
    )
};

const HeaderTdGroup = ({ desglosar }) => {

    const style = { fontStyle: "italic", fontWeight: "500" };

    return desglosar ?
        <>
            <td style={style}>Importe</td>
            <td style={style}>Descuento</td>
            <td style={style}>Condonado</td>
            <td style={style}>Recargo</td>
            <td style={style}>Pago</td>
        </>
        :
        <>
            <td style={style}>Total</td>
            <td style={style}>Pago</td>
        </>
};

const TotalesTdGroup = ({ totales, pagoActual, openModal, desglosar, onPagoInfoEnter }) => {

    const { cargo, descuento, condonacion, recargo, total, pago } = totales;

    const pagoAplicado = pagoActual ?? pago;

    const pagoCell = openModal !== undefined ?
        <PagoCell
            id={openModal ? idModalContainer : undefined}
            pago={pagoActual}
            onPointerEnter={onPagoInfoEnter}
        />
        :
        <TableMoneyFormat value={pagoAplicado} />

    return desglosar ?
        <>
            <TableMoneyFormat value={cargo} />
            <TableMoneyFormat value={descuento} />
            <TableMoneyFormat value={condonacion} />
            <TableMoneyFormat value={recargo} />
            {pagoCell}
        </>
        :
        <>
            <TableMoneyFormat value={total} />
            {pagoCell}
        </>
};

const ConcentradoTable = (props) => {

    const { informes, pagosFechas, tableSettings, enableShadow, headersInfo, extraInfo, pagosActual } = props;
    const { colTotales, colPerc, rowTotales } = extraInfo;
    const { anclarColumnas, anclarFilas, desglosarTotales } = tableSettings;

    const [pagosModalInfo, setPagosModalInfo] = useState({
        rowIndex: -1, prodIndex: -1, open: false, 
        rect: { top: 0, left: 0, width: 0 }    
    });

    const tableRef = useRef();
    const prevOffsetsWidthRef = useRef(0);

    const { rowIndex, prodIndex, open, rect } = pagosModalInfo;

    const pagosMapped = informes[rowIndex]?.productos[prodIndex].pagos.length > 0 ? 
        getPagosMapped(informes[rowIndex].productos[prodIndex].pagos, pagosFechas) 
        : [];

    const enableAnclarColumnas = anclarColumnas ? "anclar-columnas-totales" : "";
    const enableAnclarFilas = anclarFilas ? "anclar-filas-totales" : "";
    const enableDesglosarTotales = desglosarTotales ? 'desglosar-totales' : 'no-desglosar-totales';
    const enabledShadow = enableShadow ? 'enable-shadow' : '';

    const tableClassName = ['concentrados-table', enableAnclarColumnas, enableAnclarFilas, enableDesglosarTotales, enabledShadow].join(' ');

    const colSpan = desglosarTotales ? 5 : 2;

    const enabledHeaders = headersInfo ? headersInfo.filter((({ disabled }) => !disabled)) : [];

    const tableObserver = useCallback((entries) => {
        if (!entries[0]) return;

        const { target } = entries[0];

        const rowElement = target.querySelector('tbody tr');
        const lastTds = Array.from(rowElement.children).slice(-colSpan);

        const offsetsWidth = lastTds.reduce((total, td) => total + td.clientWidth, 0);

        if(offsetsWidth === prevOffsetsWidthRef.current) return;

        prevOffsetsWidthRef.current = offsetsWidth;

        let widthAcum = +Number(lastTds.pop().getBoundingClientRect().width).toFixed(2);

        lastTds.reverse().forEach((td, index) => {
            const width = +Number(td.getBoundingClientRect().width).toFixed(2);
            const property = '--offset-' + (index + 1);
            const value = widthAcum + "px";
            target.style.setProperty(property, value);
            widthAcum += width;
        });

    }, [colSpan]);
  
    useEffect(() => {

        if(enableAnclarColumnas === "") return;

        const observer = new ResizeObserver(tableObserver);
        observer.observe(tableRef.current, { box: "border-box" });

        return () => observer.disconnect();

    }, [enableAnclarColumnas, headersInfo, tableObserver]);
   
    const onPagoInfoEnter = (e, index1, index2) => {

        const bounds = e.target.getBoundingClientRect();

        setPagosModalInfo(({ rowIndex, prodIndex: prodId, rect }) => ({
            rowIndex: index1,
            prodIndex: index2,
            open: true, 
            rect: bounds
        }));
    };

    const onCloseModal = useCallback(() => { 
        
        setPagosModalInfo((prevInfo) => ({
            ...prevInfo, open: false
        }));
    }, []);

    const HeadersRow = (
        <tr>
            <th style={{ textAlign: "left" }}>Vivienda</th>
            {enabledHeaders.map(({ header }, index) => (
                <th colSpan={colSpan} key={index}>{header}</th>
            ))}
            <th colSpan={colSpan}>Total</th>
        </tr>
    );

    const SubHeadersRow = (
        <tr>
            <td></td>
            {enabledHeaders.map((info, index) => (
                <HeaderTdGroup key={index} desglosar={desglosarTotales}/>
            ))}
            <HeaderTdGroup desglosar={desglosarTotales}/>
        </tr>
    );

    const ConcentradoRows = informes.map(({ productos, id_vivienda, vivienda }, informesIndex) => (
        <tr key={id_vivienda}>
            <td style={{ fontSize: "0.93rem " }}>{vivienda}</td>
            {enabledHeaders.map(({ id }, index) => {

                const foundIndex = productos.findIndex(({ id_producto }) => id_producto === id);
                const producto = productos[foundIndex];
                return (
                    <TotalesTdGroup
                        key={index}
                        totales={producto}
                        pagoActual={pagosActual[informesIndex][producto.id_producto]}
                        openModal={rowIndex === informesIndex && prodIndex === foundIndex}
                        desglosar={desglosarTotales}
                        onPagoInfoEnter={(e) => onPagoInfoEnter(e, informesIndex, foundIndex)}
                    />
                )
            })}
            <TotalesTdGroup totales={rowTotales[id_vivienda]} desglosar={desglosarTotales} />
        </tr>
    ));

    const TotalesRow = (
        <tr>
            <td style={{ fontSize: "0.93rem" }}>Totales</td>
            {enabledHeaders.map(({ id }, index) => (
                <TotalesTdGroup key={index} totales={colTotales[id]} desglosar={desglosarTotales} />
            ))}
            <TotalesTdGroup totales={colTotales['TotalesCol']} desglosar={desglosarTotales} />
        </tr>
    );

    const CobranzaRow = (
        <tr>
            <td style={{ fontSize: "0.93rem" }}>Cobranza</td>
            {enabledHeaders.map(({ id }, index) => (
                <TablePercentageFormat key={index} value={colPerc[id]} colSpan={colSpan}/>
            ))}
            <TablePercentageFormat value={colPerc['TotalesCol']} colSpan={colSpan} />
        </tr>
    );

    const ConcentradoEmptyRow = (
        <tr className='empty-data-row'>
            <td colSpan={((enabledHeaders.length + 1) * colSpan) + 1}
                style={{ border: "unset" }}
            >
                <div >
                    <h3>Sin documentos en filtrado</h3>
                </div>
            </td>
        </tr>
    );

    return (
        <>
            <table ref={tableRef} className={tableClassName}>
                <thead>
                    {HeadersRow}
                </thead>
                <tbody>
                    {SubHeadersRow}
                    {informes.length > 0 ?
                        <>
                            {ConcentradoRows}
                            {TotalesRow}
                            {CobranzaRow}
                        </>
                        :
                        ConcentradoEmptyRow
                    }
                </tbody>
            </table>
            <PagosYSafModal
                isPagos={true}
                pagos={pagosMapped}
                open={open}
                parentInfo={{ id: idModalContainer, rect }}
                onCloseModal={onCloseModal}
            />
        </>
    )
};
